/**
 * DeviceDisplayPage — permanent display screen for Android tablet / kiosk.
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { io } from 'socket.io-client';
import { Volume2, Wifi, WifiOff, RotateCcw, Plane } from 'lucide-react';
import { createPlayDedupe } from '../../utils/playDedupe';

const SOCKET_URL = import.meta.env.VITE_SOCKET_URL || '';
const DEVICE_TOKEN_KEY = 'device_token';
const DEVICE_INFO_KEY  = 'device_info';

// ── Wake Lock helper ──────────────────────────────────────────────────────────

async function requestWakeLock(ref) {
  if (!('wakeLock' in navigator)) return;
  try {
    ref.current = await navigator.wakeLock.request('screen');
    ref.current.addEventListener('release', () => { ref.current = null; });
  } catch (_) {}
}

async function releaseWakeLock(ref) {
  if (ref.current) { await ref.current.release().catch(() => {}); ref.current = null; }
}

// ── Waveform bars (playing indicator) ────────────────────────────────────────

function WaveformBars({ active }) {
  const heights = [35, 60, 80, 50, 90, 40, 70, 55, 85, 45, 65, 30];
  return (
    <div className="flex items-end justify-center gap-[3px]" style={{ height: 36 }}>
      {heights.map((h, i) => (
        <div
          key={i}
          className="rounded-full"
          style={{
            width: 4,
            backgroundColor: active ? '#34d399' : '#334155',
            height: active ? `${h}%` : '20%',
            transition: 'height 0.3s ease',
            animation: active ? `waveBar 0.8s ease-in-out ${(i * 70) % 400}ms infinite alternate` : 'none',
          }}
        />
      ))}
    </div>
  );
}

// ── Departure countdown ───────────────────────────────────────────────────────

function useCountdown(depTime) {
  const [label, setLabel] = useState('');
  useEffect(() => {
    if (!depTime) return;
    const tick = () => {
      const diff = new Date(depTime) - Date.now();
      if (diff <= 0) { setLabel('Departed'); return; }
      const h = Math.floor(diff / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      setLabel(h > 0 ? `${h}h ${m}m` : `${m}m`);
    };
    tick();
    const t = setInterval(tick, 30000);
    return () => clearInterval(t);
  }, [depTime]);
  return label;
}

// ── DeviceDisplayPage ─────────────────────────────────────────────────────────

export default function DeviceDisplayPage() {
  const navigate = useNavigate();

  const [deviceInfo, setDeviceInfo]   = useState(null);
  const [session, setSession]         = useState(null);
  const [connected, setConnected]     = useState(false);
  const [audioUnlocked, setAudioUnlocked] = useState(false);
  const [playerStatus, setPlayerStatus]   = useState('idle');
  const [nowPlaying, setNowPlaying]   = useState(null);
  const [queueLength, setQueueLength] = useState(0);
  const [log, setLog]                 = useState([]);
  const [showReset, setShowReset]     = useState(false);

  const socketRef        = useRef(null);
  const audioRef         = useRef(new Audio());
  const queueRef         = useRef([]);
  const audioUnlockedRef = useRef(false);
  const isPlayingRef     = useRef(false);
  const suppressEndedRef = useRef(false);
  const suppressErrorRef = useRef(false);
  const isPausedRef      = useRef(false);
  const currentAnnIdRef  = useRef(null);
  const playDedupeRef    = useRef(createPlayDedupe());
  const wakeLockRef      = useRef(null);
  const nowPlayingRef    = useRef(null);

  const depCountdown = useCountdown(session?.departure_time);

  const addLog = useCallback((msg) => {
    const t = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
    setLog(prev => [`[${t}] ${msg}`, ...prev].slice(0, 40));
  }, []);

  // ── On mount ─────────────────────────────────────────────────────────────

  useEffect(() => {
    const token   = localStorage.getItem(DEVICE_TOKEN_KEY);
    const infoRaw = localStorage.getItem(DEVICE_INFO_KEY);
    if (!token) { navigate('/device-setup', { replace: true }); return; }
    if (infoRaw) { try { setDeviceInfo(JSON.parse(infoRaw)); } catch (_) {} }

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') requestWakeLock(wakeLockRef);
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [navigate]);

  // ── Audio events ──────────────────────────────────────────────────────────

  useEffect(() => {
    const audio = audioRef.current;

    const onEnded = () => {
      if (suppressEndedRef.current) { suppressEndedRef.current = false; isPlayingRef.current = false; return; }
      isPlayingRef.current = false;
      const annId = currentAnnIdRef.current;
      playDedupeRef.current.finish(annId);
      currentAnnIdRef.current = null;
      setPlayerStatus('ready');
      setNowPlaying(null);
      nowPlayingRef.current = null;
      if (annId && socketRef.current) {
        const info = deviceInfo || JSON.parse(localStorage.getItem(DEVICE_INFO_KEY) || '{}');
        if (info.device_type === 'gate') {
          socketRef.current.emit('announcement:played', { announcementId: annId, gateId: info.gate_id });
        } else {
          socketRef.current.emit('zone:played', { announcementId: annId, zoneCode: info.zone_code });
        }
      }
      releaseWakeLock(wakeLockRef);
      setTimeout(() => playNext(), 1200);
    };

    const onError = () => {
      if (suppressErrorRef.current) { suppressErrorRef.current = false; isPlayingRef.current = false; return; }
      addLog('Audio error — skipping');
      isPlayingRef.current = false;
      currentAnnIdRef.current = null;
      setPlayerStatus('ready');
      setNowPlaying(null);
      nowPlayingRef.current = null;
      releaseWakeLock(wakeLockRef);
      setTimeout(() => playNext(), 1000);
    };

    audio.addEventListener('ended', onEnded);
    audio.addEventListener('error', onError);
    return () => { audio.removeEventListener('ended', onEnded); audio.removeEventListener('error', onError); };
  }, [deviceInfo]);

  // ── Play audio ────────────────────────────────────────────────────────────

  const playAudio = useCallback((audioUrl, announcementId, text, isAdhoc = false) => {
    if (isPlayingRef.current && !isAdhoc) {
      queueRef.current.push({ audioUrl, announcementId, text, isAdhoc });
      setQueueLength(queueRef.current.length); return;
    }
    if (!audioUnlockedRef.current) {
      queueRef.current.push({ audioUrl, announcementId, text, isAdhoc });
      setQueueLength(queueRef.current.length);
      addLog('Audio locked — queued'); return;
    }
    if (isPausedRef.current && !isAdhoc) {
      queueRef.current.push({ audioUrl, announcementId, text, isAdhoc });
      setQueueLength(queueRef.current.length);
      addLog('Paused — queued'); return;
    }
    if (isAdhoc && isPlayingRef.current) {
      suppressEndedRef.current = true; suppressErrorRef.current = true;
      audioRef.current.pause();
      isPlayingRef.current = false;
      queueRef.current = []; setQueueLength(0);
    }
    const audio = audioRef.current;
    isPlayingRef.current = true;
    currentAnnIdRef.current = announcementId;
    const baseUrl = SOCKET_URL || window.location.origin;
    audio.src = audioUrl.startsWith('http') ? audioUrl : `${baseUrl}${audioUrl}`;
    audio.load();
    const np = { text, isAdhoc };
    setNowPlaying(np); nowPlayingRef.current = np;
    setPlayerStatus('playing');
    requestWakeLock(wakeLockRef);
    const info = deviceInfo || JSON.parse(localStorage.getItem(DEVICE_INFO_KEY) || '{}');
    if (announcementId && socketRef.current && info.device_type === 'gate') {
      socketRef.current.emit('announcement:playing', { announcementId, gateId: info.gate_id });
    }
    audio.play().catch(err => {
      addLog(`Playback failed: ${err.message}`);
      isPlayingRef.current = false; currentAnnIdRef.current = null;
      setPlayerStatus('ready'); setNowPlaying(null); nowPlayingRef.current = null;
    });
    addLog(`Playing: ${isAdhoc ? '[AD-HOC] ' : ''}${text?.substring(0, 60) || audioUrl}`);
  }, [deviceInfo, addLog]);

  const playNext = useCallback(() => {
    if (isPausedRef.current || isPlayingRef.current) return;
    if (queueRef.current.length === 0) {
      setPlayerStatus(audioUnlockedRef.current ? 'ready' : 'idle'); return;
    }
    const next = queueRef.current.shift();
    setQueueLength(queueRef.current.length);
    playAudio(next.audioUrl, next.announcementId, next.text, next.isAdhoc);
  }, [playAudio]);

  // ── Socket.io ─────────────────────────────────────────────────────────────

  useEffect(() => {
    const token = localStorage.getItem(DEVICE_TOKEN_KEY);
    const info  = deviceInfo;
    if (!token || !info) return;

    const socket = io(SOCKET_URL, {
      auth: { token, deviceToken: token },
      transports: ['websocket'],
      reconnection: true,
      reconnectionDelay: 3000,
      reconnectionDelayMax: 10000,
    });
    socketRef.current = socket;

    socket.on('connect', () => {
      setConnected(true);
      if (info.device_type === 'gate' && info.gate_id) {
        socket.emit('gate:register', info.gate_id);
        addLog(`Connected — gate ${info.gate_code || info.gate_id}`);
      } else if (info.device_type === 'zone' && info.zone_code) {
        socket.emit('zone:register', info.zone_code);
        addLog(`Connected — zone ${info.zone_code}`);
      }
    });

    socket.on('disconnect', (reason) => { setConnected(false); addLog(`Disconnected: ${reason}`); });

    // The admin changed this device's PIN or revoked it — the stored token is no
    // longer valid, so drop it and go back to setup to sign in again.
    const signOut = (reason) => {
      socket.io.opts.reconnection = false;
      socket.disconnect();
      localStorage.removeItem(DEVICE_TOKEN_KEY); localStorage.removeItem(DEVICE_INFO_KEY);
      navigate('/device-setup', { replace: true, state: { message: reason } });
    };
    socket.on('device:revoked', ({ reason } = {}) => signOut(reason || 'Device credentials changed — please sign in again.'));
    socket.on('connect_error', (err) => {
      if (err?.message === 'Authentication failed') signOut('This device was signed out. Please enter the Device ID and new PIN.');
    });

    socket.on('gate:command', ({ command, payload }) => {
      addLog(`Gate cmd: ${command}`);
      switch (command) {
        case 'PLAY': {
          const { audioUrl, announcementId, englishText, announcementType } = payload;
          if (!audioUrl) { if (announcementId) socket.emit('announcement:played', { announcementId, gateId: info.gate_id }); return; }
          // Same announcement re-sent (e.g. after a socket reconnect) — never play it twice
          const seen = playDedupeRef.current.check(announcementId);
          if (seen === 'active') { addLog(`Ignoring repeat PLAY for ${announcementId}`); break; }
          if (seen === 'finished') { socket.emit('announcement:played', { announcementId, gateId: info.gate_id }); break; }
          playDedupeRef.current.accept(announcementId);
          playAudio(audioUrl, announcementId, englishText || announcementType); break;
        }
        case 'PLAY_ADHOC':
        case 'PLAY_AIRPORT': {
          const { audioUrl, englishText, title } = payload || {};
          if (!audioUrl) return;
          playAudio(audioUrl, null, englishText || title, true); break;
        }
        case 'PAUSE': {
          // Emergency Pause (CR 6.1-6.3): hard-stop — no native pause, because
          // Resume must restart the interrupted announcement from the beginning.
          // The server re-sends it (same id) on Resume, so release it here.
          isPausedRef.current = true;
          const audio = audioRef.current;
          audio.pause(); audio.currentTime = 0; // pausing fires no 'ended', so nothing to suppress
          playDedupeRef.current.release(currentAnnIdRef.current);
          queueRef.current.forEach(q => playDedupeRef.current.release(q.announcementId));
          queueRef.current = []; setQueueLength(0);
          isPlayingRef.current = false; currentAnnIdRef.current = null;
          setNowPlaying(null); nowPlayingRef.current = null;
          setPlayerStatus('paused'); addLog('Paused'); break;
        }
        case 'RESUME':
          // Nothing to continue locally — the server re-dispatches the
          // interrupted announcement as a fresh PLAY
          isPausedRef.current = false;
          setPlayerStatus('ready'); playNext();
          addLog('Resumed'); break;
        case 'SKIP': {
          const audio = audioRef.current;
          if (!audio.paused) {
            suppressEndedRef.current = true; suppressErrorRef.current = true;
            const annId = currentAnnIdRef.current;
            audio.pause(); isPlayingRef.current = false; currentAnnIdRef.current = null;
            setNowPlaying(null); nowPlayingRef.current = null;
            if (annId) socket.emit('announcement:played', { announcementId: annId, gateId: info.gate_id });
            setTimeout(() => playNext(), 500);
          }
          addLog('Skipped'); break;
        }
        case 'CANCEL_SESSION':
          suppressEndedRef.current = true; suppressErrorRef.current = true;
          audioRef.current.pause(); isPlayingRef.current = false;
          queueRef.current = []; setQueueLength(0);
          setNowPlaying(null); nowPlayingRef.current = null;
          currentAnnIdRef.current = null; setPlayerStatus('ready'); setSession(null);
          addLog('Session cancelled'); break;
      }
    });

    socket.on('zone:command', ({ command, payload }) => {
      if (command === 'PLAY' && payload.audioUrl) playAudio(payload.audioUrl, payload.announcementId, payload.englishText);
      if (command === 'STOP') {
        suppressEndedRef.current = true; suppressErrorRef.current = true;
        audioRef.current.pause(); isPlayingRef.current = false;
        queueRef.current = []; setQueueLength(0);
        setNowPlaying(null); nowPlayingRef.current = null; setPlayerStatus('ready');
        addLog('Zone stopped');
      }
    });

    socket.on('session:created', (data) => { setSession(data); setPlayerStatus('ready'); addLog(`Session: ${data.flight_number}`); });
    socket.on('session:status', ({ status }) => {
      if (status === 'completed') { setSession(prev => prev ? { ...prev, status } : prev); addLog('Session completed'); }
    });

    const hb = setInterval(() => {
      if (socket.connected) socket.emit('gate:heartbeat', { gateId: info.gate_id, status: playerStatus, queueLength: queueRef.current.length });
    }, 30000);

    return () => { clearInterval(hb); socket.disconnect(); };
  }, [deviceInfo]);

  useEffect(() => {
    if (audioUnlocked && queueRef.current.length > 0 && !isPlayingRef.current) {
      addLog('Processing queue after unlock'); playNext();
    }
  }, [audioUnlocked]);

  // ── Silent auto-unlock ────────────────────────────────────────────────────

  useEffect(() => {
    const audio = audioRef.current;
    suppressEndedRef.current = true;
    audio.src = 'data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEARKwAAIhYAQACABAAZGF0YQAAAAA=';
    audio.play()
      .then(() => {
        audio.pause();
        audioUnlockedRef.current = true; setAudioUnlocked(true); setPlayerStatus('ready');
        requestWakeLock(wakeLockRef); addLog('Audio auto-unlocked');
      })
      .catch(() => { suppressEndedRef.current = false; });
  }, []);

  // ── Manual unlock + fullscreen ────────────────────────────────────────────

  function unlockAudio() {
    const audio = audioRef.current;
    suppressEndedRef.current = true;
    audio.src = 'data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEARKwAAIhYAQACABAAZGF0YQAAAAA=';
    audio.play()
      .then(() => {
        audio.pause();
        audioUnlockedRef.current = true; setAudioUnlocked(true); setPlayerStatus('ready');
        addLog('Audio activated');
        document.documentElement.requestFullscreen?.().catch(() => {});
        requestWakeLock(wakeLockRef);
      })
      .catch(err => { suppressEndedRef.current = false; addLog(`Activation failed: ${err.message}`); });
  }

  function resetDevice() {
    localStorage.removeItem(DEVICE_TOKEN_KEY); localStorage.removeItem(DEVICE_INFO_KEY);
    navigate('/device-setup', { replace: true });
  }

  // ── Derived display values ────────────────────────────────────────────────

  const info = deviceInfo;
  const isGate = info?.device_type === 'gate';

  const displayLabel = isGate
    ? (info.gate_code || `Gate ${info.gate_id}`)
    : info?.zone_code === 'ALL' ? 'Airport Wide' : (info?.zone_code || 'Zone');

  const terminalLabel = isGate
    ? (info.gate_terminal ? `Terminal ${info.gate_terminal}` : null)
    : 'Zone Display';

  const isPlaying = playerStatus === 'playing';
  const isPaused  = playerStatus === 'paused';

  const depTime = session?.departure_time
    ? new Date(session.departure_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    : null;

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <>
      {/* Keyframe injection */}
      <style>{`
        @keyframes waveBar {
          from { transform: scaleY(0.3); }
          to   { transform: scaleY(1); }
        }
        @keyframes fadeSlideUp {
          from { opacity: 0; transform: translateY(12px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes breathe {
          0%, 100% { opacity: 0.6; }
          50%       { opacity: 1; }
        }
      `}</style>

      <div className="min-h-screen flex flex-col select-none overflow-hidden"
        style={{ background: 'linear-gradient(160deg, #080f1e 0%, #0d1527 50%, #080f1e 100%)' }}>

        {/* ── Top bar ── */}
        <div className="flex items-center justify-between px-6 py-3"
          style={{ borderBottom: '1px solid rgba(255,255,255,0.06)', background: 'rgba(0,0,0,0.3)', backdropFilter: 'blur(12px)' }}>

          {/* Connection status */}
          <div className="flex items-center gap-2.5">
            <div className="relative flex items-center justify-center w-5 h-5">
              <div className={`w-2 h-2 rounded-full ${connected ? 'bg-emerald-400' : 'bg-red-400'}`} />
              {connected && (
                <div className="absolute w-4 h-4 rounded-full border border-emerald-400 animate-ping opacity-40" />
              )}
            </div>
            <span className="text-xs font-semibold tracking-[0.2em] uppercase"
              style={{ color: connected ? '#6ee7b7' : '#f87171' }}>
              {connected ? 'Online' : 'Reconnecting…'}
            </span>
          </div>

          {/* Device name + hidden reset trigger */}
          <div className="flex items-center gap-1">
            <span className="text-xs tracking-widest uppercase" style={{ color: 'rgba(255,255,255,0.25)' }}>
              {info?.device_name || info?.device_id || '…'}
            </span>
            <button className="w-8 h-8 opacity-0 cursor-default" onClick={() => setShowReset(s => !s)} />
          </div>
        </div>

        {/* ── Main content ── */}
        <div className="flex-1 flex flex-col items-center justify-center px-8 py-10 gap-8">

          {/* Gate / Zone code */}
          <div className="text-center">
            <div
              className="font-black leading-none tracking-tight"
              style={{
                fontSize: 'clamp(5rem, 18vw, 10rem)',
                color: isPlaying ? '#ffffff' : 'rgba(255,255,255,0.9)',
                textShadow: isPlaying ? '0 0 60px rgba(52,211,153,0.3)' : 'none',
                transition: 'all 0.6s ease',
                letterSpacing: '-0.02em',
              }}
            >
              {displayLabel}
            </div>
            {terminalLabel && (
              <div className="mt-2 text-xs font-semibold tracking-[0.35em] uppercase"
                style={{ color: 'rgba(255,255,255,0.3)' }}>
                {terminalLabel}
              </div>
            )}
          </div>

          {/* Flight info card */}
          {isGate && session && (
            <div
              className="w-full max-w-sm rounded-2xl px-6 py-5 text-center"
              style={{
                background: 'rgba(255,255,255,0.05)',
                border: '1px solid rgba(255,255,255,0.1)',
                backdropFilter: 'blur(16px)',
                animation: 'fadeSlideUp 0.5s ease',
              }}
            >
              <div className="flex items-center justify-center gap-2 mb-3">
                <Plane size={14} className="text-blue-400 rotate-45" />
                <span className="text-2xl font-black text-white tracking-wide">{session.flight_number}</span>
              </div>
              {session.airline && (
                <div className="text-xs font-medium tracking-[0.15em] uppercase mb-3"
                  style={{ color: 'rgba(255,255,255,0.45)' }}>
                  {session.airline}
                </div>
              )}
              <div
                className="text-base font-bold mb-3 tracking-wide"
                style={{ color: 'rgba(255,255,255,0.85)' }}
              >
                {session.destination}
                {session.destination_code && (
                  <span className="ml-2 text-xs font-semibold px-2 py-0.5 rounded-full"
                    style={{ background: 'rgba(96,165,250,0.15)', color: '#93c5fd' }}>
                    {session.destination_code}
                  </span>
                )}
              </div>
              {depTime && (
                <div className="flex items-center justify-center gap-3 text-xs">
                  <span style={{ color: 'rgba(255,255,255,0.4)' }}>Departure</span>
                  <span className="font-bold text-white">{depTime}</span>
                  {depCountdown && depCountdown !== 'Departed' && (
                    <>
                      <span style={{ color: 'rgba(255,255,255,0.2)' }}>·</span>
                      <span className="font-semibold px-2 py-0.5 rounded-full text-xs"
                        style={{ background: 'rgba(96,165,250,0.15)', color: '#93c5fd' }}>
                        {depCountdown}
                      </span>
                    </>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Status + Now Playing */}
          <div className="text-center w-full max-w-lg">

            {/* Waveform + status label */}
            <div className="flex flex-col items-center gap-3 mb-4">
              <WaveformBars active={isPlaying} />
              <div className="flex items-center gap-2">
                {isPaused ? (
                  <span className="text-xs font-black tracking-[0.3em] uppercase text-amber-400">Paused</span>
                ) : isPlaying ? (
                  <span className="text-xs font-black tracking-[0.3em] uppercase text-emerald-400">Playing</span>
                ) : (
                  <span className="text-xs font-semibold tracking-[0.25em] uppercase"
                    style={{ color: 'rgba(255,255,255,0.25)', animation: 'breathe 3s ease-in-out infinite' }}>
                    {playerStatus === 'idle' ? 'Standby' : 'Ready'}
                  </span>
                )}
              </div>
            </div>

            {/* Announcement text */}
            {nowPlaying ? (
              <div style={{ animation: 'fadeSlideUp 0.4s ease' }}>
                {nowPlaying.isAdhoc && (
                  <div className="inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1 rounded-full mb-3"
                    style={{ background: 'rgba(249,115,22,0.15)', color: '#fb923c', border: '1px solid rgba(249,115,22,0.25)' }}>
                    Ad-hoc Announcement
                  </div>
                )}
                <p className="text-lg font-medium leading-relaxed"
                  style={{ color: 'rgba(255,255,255,0.88)', maxWidth: 520, margin: '0 auto' }}>
                  "{nowPlaying.text}"
                </p>
              </div>
            ) : (
              !isPlaying && (
                <p className="text-sm" style={{ color: 'rgba(255,255,255,0.15)' }}>
                  {playerStatus === 'idle' ? 'Waiting for session…' : 'Waiting for next announcement…'}
                </p>
              )
            )}

            {/* Queue badge */}
            {queueLength > 0 && (
              <div className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1 rounded-full mt-4"
                style={{ background: 'rgba(255,255,255,0.07)', color: 'rgba(255,255,255,0.4)', border: '1px solid rgba(255,255,255,0.08)' }}>
                {queueLength} queued
              </div>
            )}
          </div>
        </div>

        {/* ── Activity log ── */}
        <div style={{ borderTop: '1px solid rgba(255,255,255,0.05)', background: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(8px)' }}
          className="px-5 py-3 max-h-28 overflow-y-auto">
          <div className="text-[10px] font-bold tracking-[0.2em] uppercase mb-1.5"
            style={{ color: 'rgba(255,255,255,0.15)' }}>
            Activity
          </div>
          {log.length === 0
            ? <div className="text-[11px]" style={{ color: 'rgba(255,255,255,0.12)' }}>No activity yet</div>
            : log.map((entry, i) => (
              <div key={i} className="text-[11px] leading-5 font-mono"
                style={{ color: i === 0 ? 'rgba(255,255,255,0.35)' : 'rgba(255,255,255,0.15)' }}>
                {entry}
              </div>
            ))
          }
        </div>

        {/* ── Audio unlock overlay ── */}
        {!audioUnlocked && (
          <div onClick={unlockAudio}
            className="fixed inset-0 z-40 flex flex-col items-end justify-end pb-12 pr-0 cursor-pointer"
            style={{ background: 'rgba(8,15,30,0.6)', backdropFilter: 'blur(2px)' }}>
            <div className="w-full flex flex-col items-center gap-3 pb-4"
              style={{ animation: 'breathe 2s ease-in-out infinite' }}>
              <div className="flex items-center justify-center w-14 h-14 rounded-full shadow-2xl"
                style={{ background: 'rgba(59,130,246,0.8)', boxShadow: '0 0 40px rgba(59,130,246,0.5)' }}>
                <Volume2 className="w-7 h-7 text-white" />
              </div>
              <span className="text-sm font-bold tracking-[0.25em] uppercase"
                style={{ color: 'rgba(255,255,255,0.7)' }}>
                Tap anywhere to activate
              </span>
            </div>
          </div>
        )}

        {/* ── Reset modal ── */}
        {showReset && (
          <div className="fixed inset-0 z-50 flex items-center justify-center"
            style={{ background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(8px)' }}>
            <div className="w-full max-w-xs mx-6 rounded-3xl p-8 text-center"
              style={{ background: '#0d1527', border: '1px solid rgba(255,255,255,0.12)' }}>
              <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-5"
                style={{ background: 'rgba(245,158,11,0.15)', border: '1px solid rgba(245,158,11,0.3)' }}>
                <RotateCcw className="w-6 h-6 text-amber-400" />
              </div>
              <h2 className="text-white text-xl font-black mb-2">Reset Device?</h2>
              <p className="text-sm mb-8" style={{ color: 'rgba(255,255,255,0.45)' }}>
                This will clear the device token and return to the setup screen.
              </p>
              <div className="flex gap-3">
                <button onClick={() => setShowReset(false)}
                  className="flex-1 py-3.5 rounded-2xl text-sm font-bold transition-colors"
                  style={{ background: 'rgba(255,255,255,0.07)', color: 'rgba(255,255,255,0.6)', border: '1px solid rgba(255,255,255,0.1)' }}>
                  Cancel
                </button>
                <button onClick={resetDevice}
                  className="flex-1 py-3.5 rounded-2xl text-sm font-bold transition-colors"
                  style={{ background: 'rgba(239,68,68,0.2)', color: '#f87171', border: '1px solid rgba(239,68,68,0.3)' }}>
                  Reset
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
